/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/RetrasosProfesorPage.tsx
   ────────────────────────────────────────────────────────────── */
import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
  Button,
  Chip,
  Box,
  Tooltip,
  IconButton,
  Collapse,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { useRetrasosProfesorReport } from '../../../hooks/useReportJob';
import { getReservasRetrasoProfesor } from '../../../api/reports';
import type { RetrasosProfesorRow, RetrasoReservaRow } from '../../../api/reports';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function RetrasosProfesorPage() {
  const { t } = useTranslation<'translation'>();
  /* ═════════ filtros de entrada ═════════ */
  const today = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today);
  const [search, setSearch] = useState('');

  /* ═════════ job resumen (retrasos por profesor) ═════════ */
  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useRetrasosProfesorReport();
  const { data: status } = useStatus(jobId);

  /* ───── generar informe ───── */
  const launch = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error('Error al descargar:', error);
      }
    }
  };

  /* ───── resumen filtrado por búsqueda ───── */
  const rows =
    status?.result?.rows.filter((r: RetrasosProfesorRow) =>
      r.profesor.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  /* ═════════ helpers UI ═════════ */
  const formatMinutos = (min: number) => {
    const horas = (Number(min) || 0) / 60;
    return horas.toLocaleString('en-US', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const getTasaColor = (tasa: number) => {
    if (tasa >= 20) return 'error';
    if (tasa >= 10) return 'warning';
    return 'success';
  };

  /* ═════════ componente Row con colapso ═════════ */
  function Row({ row, desde, hasta }: { row: RetrasosProfesorRow; desde: string; hasta: string }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reservas, setReservas] = useState<RetrasoReservaRow[]>([]);
    const { t } = useTranslation<'translation'>();

    const handleExpand = async () => {
      if (!open && reservas.length === 0) {
        setLoading(true);
        try {
          const data = await getReservasRetrasoProfesor(row.profesorId, desde, hasta);
          setReservas(data);
        } catch (error) {
          // Error cargando reservas
        } finally {
          setLoading(false);
        }
      }
      setOpen(!open);
    };

    return (
      <>
        <TableRow hover>
          <TableCell padding="checkbox">
            <IconButton size="small" onClick={handleExpand}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="medium">
              {row.profesor}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Chip label={row.numRetrasos} size="small" color="primary" variant="outlined" />
          </TableCell>
          <TableCell align="right">
            <Tooltip title={`${row.tiempoTotalMin} minutos`}>
              <Typography variant="body2" fontWeight="medium">
                {formatMinutos(row.tiempoTotalMin)}
              </Typography>
            </Tooltip>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2" color="text.secondary">
              {formatMinutos(Math.round(row.tiempoPromedioMin))}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2" color="text.secondary">
              {formatMinutos(row.tiempoMaxMin)}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Chip
              label={`${row.tasaRetrasos.toFixed(1)}%`}
              size="small"
              color={getTasaColor(row.tasaRetrasos)}
              variant={row.tasaRetrasos >= 10 ? 'filled' : 'outlined'}
            />
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2" color="text.secondary">
              {row.totalReservas}
            </Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={8} sx={{ p: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 2 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : reservas.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>{t('retrasosProfesor.detalle.fecha', 'Fecha')}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{t('retrasosProfesor.detalle.hora', 'Hora')}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{t('retrasosProfesor.detalle.cliente', 'Cliente')}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{t('retrasosProfesor.detalle.asignatura', 'Asignatura')}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{t('retrasosProfesor.detalle.retraso', 'Retraso')}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{t('retrasosProfesor.detalle.estado', 'Estado')}</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reservas.map((reserva) => (
                        <TableRow key={reserva.id}>
                          <TableCell>{dayjs(reserva.fecha).format('DD-MM-YYYY')}</TableCell>
                          <TableCell>
                            {reserva.horaInicio.slice(0, 5)} - {reserva.horaFin.slice(0, 5)}
                          </TableCell>
                          <TableCell>{reserva.cliente}</TableCell>
                          <TableCell>{reserva.asignatura}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatMinutos(reserva.retrasoMin)}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{reserva.estado}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('retrasosProfesor.detalle.sinReservas', 'No hay reservas con retraso')}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  /* ═════════ UI ═════════ */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('retrasosProfesor.titulo', 'Retrasos de Profesores')}</Typography>

      {/* ─── barra de filtros ─── */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          label={t('retrasosProfesor.filtros.desde', 'Desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label={t('retrasosProfesor.filtros.hasta', 'Hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          placeholder={t('retrasosProfesor.buscar', 'Buscar profesor...') as string}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 300 }}
        />
        <Button
          variant="contained"
          onClick={launch}
          disabled={!desde || !hasta || status?.progress === 100}
          startIcon={<SearchIcon />}
        >
          {t('retrasosProfesor.generar', 'Generar Informe')}
        </Button>
      </Stack>

      {/* ─── progreso ─── */}
      {status && !status.done && !status.failed && (
        <Box>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('retrasosProfesor.procesando', 'Procesando...')} {status.progress}%
          </Typography>
        </Box>
      )}

      {/* ─── error ─── */}
      {status?.failed && (
        <Alert severity="error">
          {t(
            'retrasosProfesor.error',
            'Error al generar el informe. Por favor, inténtalo de nuevo.',
          )}
        </Alert>
      )}

      {/* ─── resultados ─── */}
      {status?.done && status.result && (
        <Stack spacing={2}>
          {/* ─── resumen ─── */}
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t('retrasosProfesor.resultados', 'Resultados')}
              </Typography>
              <Chip
                label={`${status.result.registros} ${t('retrasosProfesor.profesores', 'profesores')}`}
                color="primary"
                variant="outlined"
              />
              {status.result.url && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  variant="outlined"
                >
                  {t('retrasosProfesor.descargar', 'Descargar Excel')}
                </Button>
              )}
            </Stack>
          </Paper>

          {/* ─── tabla ─── */}
          {rows.length > 0 ? (
            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>
                      <strong>{t('retrasosProfesor.tabla.profesor', 'Profesor')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosProfesor.tabla.numRetrasos', 'Nº Retrasos')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosProfesor.tabla.tiempoTotal', 'Tiempo Total')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosProfesor.tabla.tiempoPromedio', 'Promedio')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosProfesor.tabla.tiempoMaximo', 'Máximo')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosProfesor.tabla.tasaRetrasos', 'Tasa Retrasos')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosProfesor.tabla.totalReservas', 'Total Reservas')}</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: RetrasosProfesorRow) => (
                    <Row key={row.profesorId} row={row} desde={desde} hasta={hasta} />
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Alert severity="info">
              {t(
                'retrasosProfesor.sinResultados',
                'No se encontraron retrasos en el periodo seleccionado.',
              )}
            </Alert>
          )}
        </Stack>
      )}
    </Stack>
  );
}
